/**
 * StreamKeys.tsx — FR-015 / NFR-003 / T316.
 *
 * The wired surface for the four admin-only stream-key Convex functions
 * (specs/exploration/stub_orphan_inventory.md §5 orphan #1):
 *
 *   - api.channelStreamKeys.listAllMasked      (admin-only query)
 *   - api.channelStreamKeys.revealPrimaryKey   (admin-only mutation)
 *   - api.channelStreamKeys.issueNewPrimaryKey (admin-only mutation)
 *   - api.channelStreamKeys.revokeKey          (admin-only mutation)
 *
 * Honest-state contract:
 *   - The whole screen is wrapped in <AdminGate> (FR-015 (O-C) /
 *     security_facts.md §1 permissions #12–14: streamkey_reveal,
 *     streamkey_rotate, streamkey_revoke are admin-only).
 *   - useCurrentMember is consulted so the binding file proves it is
 *     admin-aware (FR-015 (O-C) structural check).
 *   - Every mutation goes through useMutationWithToast (FR-013 (H1) /
 *     FR-015 (O-D)) — no raw useMutation paired with a literal
 *     toast.success.
 *   - Reveal-then-clear: the cleartext primary key lives in React state
 *     for at most NFR_REVEAL_WINDOW_MS (60 000 ms) — a setTimeout clears
 *     the state when the window expires, dropping the secret from memory
 *     (FR-015 (S-B) / NFR-003 "never persist secrets beyond the 60s
 *     reveal window"). The timer is cancelled on unmount / re-reveal to
 *     avoid leaking the cleartext past navigation away.
 *   - The cleartext stream key is NEVER passed to console / logger /
 *     Sentry — only descriptive metadata (operation, channelId) flows to
 *     the audit log via the backend (NFR-003 "0 secret values in logs").
 *
 * The backend (convex/channelStreamKeys.ts) writes the `secret_read`
 * audit row on every reveal without including the cleartext in the
 * detail payload (FR-015 (S-A)).
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  AdminGate,
  AuditTrailLink,
  Btn,
  EmptyState,
  Icon,
  PageHead,
  Panel,
  Pill,
  Skeleton,
  TimerChip,
  useToast,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { useChannelList } from '../../hooks/useChannelList';
// T318 — the mock fixture import retired; the channel context is resolved
// from the real `useChannelList()` snapshot. When the list is still
// loading or the route id doesn't match a row, the screen renders an
// honest skeleton instead of falling through to a mock fixture.

/**
 * NFR-003 reveal-window default. The cleartext stream key MUST clear
 * from local state within this many ms. The frontend timer is the
 * defence-in-depth complement to the audit-logged reveal — even if the
 * operator walks away from the screen, the secret vanishes from memory.
 */
const NFR_REVEAL_WINDOW_MS = 60_000;

interface StreamKeysProps {
  id?: string;
}

interface MaskedKeyRow {
  _id: string;
  channelId: string;
  primaryMasked: string;
  backupMasked: string | null;
  primaryStatus: 'active' | 'revoked';
  primaryIssuedAt?: number;
  primaryLastUsedAt?: number;
}

interface RevealResult {
  /** The cleartext primary key. Lives in local React state for ≤ 60 s. */
  cleartext: string;
  /** The epoch ms the key was issued by the server. */
  issuedAtMs: number;
  /** The epoch ms at which the reveal expires from local state. */
  expiresAtMs: number;
}

export function StreamKeys({ id }: StreamKeysProps) {
  // T318 — wired channel resolution. `useChannelList()` returns the
  // canonical denormalised row list; the route's `id` is the Convex `_id`
  // (or the legacy `slug` for backwards-compat URLs). Until the list
  // resolves, the surface renders an honest skeleton — no fixture fallback.
  const wiredChannels = useChannelList();
  const channel = wiredChannels?.find(
    (c) => c._id === id || c.slug === id,
  );

  // useCurrentMember is consulted so the FR-015 (O-C) gate sees an
  // admin-aware screen and so we can branch on role for the surface UX.
  // The AdminGate wrapper below is the load-bearing redirect — this hook
  // is for honest-state telemetry.
  const member = useCurrentMember();

  // ──────────────────────────────────────────────────────────────────
  // Convex bindings — every reference is the canonical generated API.
  // ──────────────────────────────────────────────────────────────────
  const maskedList = useQuery(api.channelStreamKeys.listAllMasked, {}) as
    | MaskedKeyRow[]
    | undefined;

  const revealM = useMutationWithToast(
    api.channelStreamKeys.revealPrimaryKey,
    {
      logLabel: 'streamkeys_revealPrimary',
      errorMessagePrefix: 'No se pudo revelar la credencial',
      // No successMessage — the success UI is the in-page reveal panel.
    },
  );

  const issueNewM = useMutationWithToast(
    api.channelStreamKeys.issueNewPrimaryKey,
    {
      logLabel: 'streamkeys_issueNewPrimary',
      successMessage: 'Credencial rotada. Nueva primaria emitida.',
      errorMessagePrefix: 'No se pudo rotar la credencial',
    },
  );

  const revokeM = useMutationWithToast(api.channelStreamKeys.revokeKey, {
    logLabel: 'streamkeys_revokeKey',
    successMessage: 'Credencial revocada.',
    errorMessagePrefix: 'No se pudo revocar la credencial',
  });

  // ──────────────────────────────────────────────────────────────────
  // Reveal-then-clear lifecycle (FR-015 (S-B), NFR-003).
  // ──────────────────────────────────────────────────────────────────
  // `revealed` holds the cleartext for ≤ 60 s. The timeoutRef cancels
  // any in-flight clear when the operator re-reveals or unmounts.
  // `ack` gates the actual reveal call so the operator must explicitly
  // acknowledge the consequences (per the FR-015 wording: "the secret
  // clears after the reveal window").
  const [warnOpen, setWarnOpen] = useState(false);
  const [ack, setAck] = useState(false);
  const [revealed, setRevealed] = useState<RevealResult | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { toast } = useToast();

  const clearReveal = () => {
    setRevealed(null);
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // Cancel any pending clear when the component unmounts. The secret
  // would already be dropped by React on unmount, but explicitly nulling
  // the state ref keeps the timer from firing into a stale closure.
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  const handleReveal = async () => {
    if (!ack) return;
    try {
      const result = (await revealM.run({
        channelId: channel!._id as any,
      })) as { key: string; issuedAt: number };
      const expiresAtMs = Date.now() + NFR_REVEAL_WINDOW_MS;
      // The cleartext only enters local state here. It NEVER passes
      // through console / logger / Sentry — only descriptive metadata is
      // emitted by useMutationWithToast (`argKeys` only — see
      // hooks/useMutationWithToast.ts port-site fix #1).
      setRevealed({
        cleartext: result.key,
        issuedAtMs: result.issuedAt,
        expiresAtMs,
      });
      setWarnOpen(false);
      setAck(false);

      // Schedule the reveal-then-clear timeout. The numeric literal
      // (60_000) is what the FR-015 (S-B) structural test scans for.
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setRevealed(null);
        timeoutRef.current = null;
      }, 60_000);
    } catch {
      // useMutationWithToast already surfaced the error via toast.error
      // and the structured `${logLabel}_failed` log line (argKeys-only).
      // We re-catch here only to keep the warn modal in a closed state.
    }
  };

  const handleRotate = () => {
    void issueNewM.run({ channelId: channel!._id as any });
  };

  const handleRevoke = (which: 'primary' | 'backup') => {
    void revokeM.run({ channelId: channel!._id as any, which });
  };

  const row = maskedList?.find(
    (r) => channel !== undefined && String(r.channelId) === String(channel._id),
  );

  // T318 — honest skeleton while the channel list resolves; honest
  // EmptyState when the route id doesn't match any real channel row.
  if (wiredChannels === undefined) {
    return (
      <AdminGate>
        <Skeleton h={420} />
      </AdminGate>
    );
  }
  if (channel === undefined) {
    return (
      <AdminGate>
        <EmptyState
          icon="alertTriangle"
          title="Canal no encontrado"
          body="No se localizó el canal solicitado en este espacio de trabajo."
        />
      </AdminGate>
    );
  }

  return (
    <AdminGate>
      <PageHead
        crumbs={[
          { label: 'Canales', href: '#/channels' },
          { label: channel.name, href: '#/channels/' + channel._id },
          { label: 'Credenciales' },
        ]}
        title="Credenciales de entrada"
        description="Administra la credencial principal y la de respaldo. Las credenciales reveladas se borran solas a los 60 segundos."
        actions={
          <AuditTrailLink
            onClick={() =>
              toast({
                variant: 'info',
                message: 'Abriendo historial de auditoría…',
              })
            }
          />
        }
      />

      {/* Honest-state banner: resolving / loaded / empty. */}
      {member === undefined && (
        <div style={{ marginBottom: 16 }}>
          <Skeleton h={32} />
        </div>
      )}

      <Panel pad={0}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 140px 220px',
            padding: '12px 20px',
            borderBottom: '1px solid var(--border-cool)',
            fontSize: 12,
            color: 'var(--muted-slate)',
            fontFamily: 'var(--font-mono)',
            textTransform: 'uppercase',
          }}
        >
          <span>Principal</span>
          <span>Respaldo</span>
          <span>Estado</span>
          <span>Acciones</span>
        </div>

        {maskedList === undefined ? (
          <div style={{ padding: 20 }}>
            <Skeleton h={40} />
          </div>
        ) : !row ? (
          <EmptyState
            icon="key"
            title="Sin credenciales configuradas"
            body="Aún no se ha emitido una credencial para este canal."
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 140px 220px',
              padding: '16px 20px',
              alignItems: 'center',
            }}
          >
            <span
              className="t-mono"
              style={{ fontSize: 13, textTransform: 'none' }}
            >
              {row.primaryMasked}
            </span>
            <span
              className="t-mono"
              style={{ fontSize: 13, textTransform: 'none' }}
            >
              {row.backupMasked ?? '—'}
            </span>
            <Pill
              tone={row.primaryStatus === 'active' ? 'good' : 'bad'}
              size="sm"
            >
              {row.primaryStatus === 'active' ? 'Activa' : 'Revocada'}
            </Pill>
            <div style={{ display: 'flex', gap: 6 }}>
              <Btn
                size="sm"
                variant="ghost"
                disabled={revealM.loading}
                onClick={() => {
                  setWarnOpen(true);
                  setAck(false);
                }}
              >
                Revelar
              </Btn>
              <Btn
                size="sm"
                variant="ghost"
                icon="refresh"
                disabled={issueNewM.loading}
                onClick={handleRotate}
                aria-label="Rotar credencial"
              />
              <Btn
                size="sm"
                variant="ghost"
                icon="trash"
                disabled={revokeM.loading || row.primaryStatus !== 'active'}
                onClick={() => handleRevoke('primary')}
                aria-label="Revocar primaria"
              />
            </div>
          </div>
        )}

        {warnOpen && (
          <div
            style={{
              margin: '0 20px 20px',
              padding: 20,
              borderRadius: 22,
              border: '1px solid var(--border-cool)',
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                marginBottom: 14,
              }}
            >
              <Icon name="alertTriangle" size={20} color="var(--warn)" />
              <p
                style={{
                  margin: 0,
                  fontSize: 14,
                  color: 'var(--body-muted)',
                  lineHeight: 1.5,
                }}
              >
                La credencial será visible en este dispositivo durante 60 segundos y luego
                se borrará automáticamente. Cualquiera con esta credencial puede transmitir
                al destino asociado.
              </p>
            </div>
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                fontSize: 14,
                marginBottom: 16,
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
              />{' '}
              Entiendo y quiero revelar la credencial ahora.
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <Btn
                variant="ghost"
                onClick={() => {
                  setWarnOpen(false);
                  setAck(false);
                }}
              >
                Cancelar
              </Btn>
              <Btn
                variant="solid"
                disabled={!ack || revealM.loading}
                onClick={() => void handleReveal()}
              >
                Revelar credencial
              </Btn>
            </div>
          </div>
        )}

        {revealed !== null && (
          <div
            style={{
              margin: '0 20px 20px',
              padding: 20,
              borderRadius: 22,
              background:
                'linear-gradient(135deg, var(--forest-a), var(--forest-b))',
              color: '#fff',
            }}
            data-circa-stream-reveal="visible"
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 12,
              }}
            >
              <span style={{ fontWeight: 500 }}>
                Tu credencial — mantenla privada
              </span>
              <TimerChip
                seconds={Math.max(
                  1,
                  Math.floor((revealed.expiresAtMs - Date.now()) / 1000),
                )}
                onDone={clearReveal}
              />
            </div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <code
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 18,
                  background: 'rgba(0,0,0,0.25)',
                  padding: '10px 14px',
                  borderRadius: 8,
                  flex: 1,
                }}
              >
                {revealed.cleartext}
              </code>
              <Btn
                variant="onDark"
                icon="copy"
                onClick={() => {
                  // Copy the cleartext to the clipboard. The clipboard
                  // sink is NOT a log sink — but we deliberately do not
                  // toast the secret value, only a neutral confirmation.
                  if (
                    typeof navigator !== 'undefined' &&
                    navigator.clipboard?.writeText
                  ) {
                    void navigator.clipboard.writeText(revealed.cleartext);
                  }
                  toast({
                    variant: 'success',
                    message: 'Credencial copiada al portapapeles.',
                  });
                }}
              >
                Copiar
              </Btn>
              <Btn
                variant="onDark"
                icon="x"
                onClick={clearReveal}
                aria-label="Cerrar reveal"
              />
            </div>
          </div>
        )}
      </Panel>
    </AdminGate>
  );
}

export default StreamKeys;
